'use client';

import { Link } from '@tanstack/react-router';

import { BackgroundVideo } from '@/components/media/responsive-video';
import { Button } from '@/components/button';
import { Eyebrow } from '@/components/editorial';
import { Icons } from '@/components/icons';
import type { PublicProjectRecord } from '@/content/schemas';

import { ContactCta, FeaturedProjectCard } from './featured-project-card';

type HomeProjectsPageProps = {
  featuredProjects: PublicProjectRecord[];
};

export function HomeProjectsPage({ featuredProjects }: HomeProjectsPageProps) {
  return (
    <div className="bg-grain w-full">
      <section
        id="home"
        className="relative flex min-h-[70vh] w-full scroll-mt-28 items-center overflow-hidden py-24 sm:py-28"
      >
        <BackgroundVideo
          mp4Src="/videos/hero.mp4"
          webmSrc="/videos/hero.webm"
        />

        <div
          aria-hidden="true"
          className="from-background via-background/80 to-background/10 absolute inset-0 -z-10 bg-gradient-to-t"
        />

        <div className="relative z-10 mx-auto w-full max-w-6xl px-6">
          <Eyebrow as="span" className="mb-4">
            Jakarta · targeting Singapore remote · GMT+7
          </Eyebrow>
          <h1 className="font-display text-display-xl text-ink max-w-4xl font-bold leading-none">
            Fullstack AI Engineer building{' '}
            <span className="text-brand">production systems</span> with
            measurable outcomes.
          </h1>
          <p className="text-muted-foreground mt-6 max-w-2xl text-pretty text-base leading-relaxed md:text-lg">
            I ship backends, full stack products, and AI native workflows for
            teams that need evidence, not demos. Selected case studies below;
            the full archive lives on the projects index.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ContactCta />
            <Button variant="outline-brand" size="pill" asChild>
              <Link to="/projects">Browse all projects</Link>
            </Button>
          </div>
          <div className="mt-6 flex items-center gap-4">
            <a
              href="https://github.com/alfadlirt"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="text-muted-foreground hover:text-brand transition-colors"
            >
              <Icons.github className="size-5" />
            </a>
            <a
              href="mailto:alfadli.rai@gmail.com"
              aria-label="Email"
              className="text-muted-foreground hover:text-brand transition-colors"
            >
              <Icons.mail className="size-5" />
            </a>
          </div>
        </div>
      </section>

      <section
        id="selected-work"
        aria-labelledby="selected-work-heading"
        className="mx-auto w-full max-w-6xl scroll-mt-28 px-6 py-16 md:py-24"
      >
        <div className="mb-10 max-w-2xl">
          <Eyebrow className="mb-3">Selected work</Eyebrow>
          <h2
            id="selected-work-heading"
            className="font-display text-display-md text-ink font-bold"
          >
            Case studies with route-ready evidence
          </h2>
          <p className="text-muted-foreground mt-3 text-sm leading-relaxed md:text-base">
            Only published featured case studies appear here. Each card links to
            a full write-up with role, media, and sourced outcomes when present.
          </p>
        </div>

        {featuredProjects.length === 0 ? (
          <p className="text-muted-foreground font-mono text-sm">
            No featured case studies are published yet.
          </p>
        ) : (
          <div className="flex flex-col gap-8">
            {featuredProjects.map((project, index) => (
              <FeaturedProjectCard
                key={project.id}
                project={project}
                index={index + 1}
              />
            ))}
          </div>
        )}

        <div className="border-border bg-muted/30 mt-12 rounded-2xl border p-6 md:p-8">
          <p className="font-display text-ink text-xl font-semibold">
            Want to dig deeper or start a conversation?
          </p>
          <p className="text-muted-foreground mt-2 max-w-xl text-sm leading-relaxed">
            Browse the complete archive or reach out directly for Singapore
            remote, contract, and high-impact product work.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <ContactCta label="Get in touch" />
            <Button variant="outline-brand" size="pill" asChild>
              <Link to="/projects">View project archive</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
